import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSEO } from "@/hooks/use-seo";
import { Phone, Clock, Info, ExternalLink } from "lucide-react";

interface Payer {
  name: string;
  phone?: string;
  hours?: string;
  info?: string;
  stediId?: string;
}

interface StediData {
  avatarUrl?: string;
  website?: string;
}

const STEDI_API_KEY = import.meta.env.VITE_STEDI_API_KEY;

const payers: Payer[] = [
  // BCBS State-by-State
  { name: "BCBS Alabama", phone: "+1 800-517-6425", stediId: "DCIYM" },
  { name: "BCBS Alaska", phone: "+1 800-722-4714", stediId: "FKAUR" },
  { name: "BCBS Arizona", phone: "Phoenix: +1 602-864-4400 / +1 800-232-2345, Tucson: +1 800-752-0193, Flagstaff: +1 928-526-0232 / +1 800-423-6484", stediId: "KHTHB" },
  { name: "BCBS Arkansas", phone: "+1 800-827-4814", stediId: "GESBC" },
  { name: "BCBS California", phone: "+1 800-677-6669", stediId: "LAESW" },
  { name: "BCBS Colorado", phone: "+1 888-817-3717", stediId: "TBEZC" },
  { name: "BCBS Connecticut", phone: "+1 800-934-0331", stediId: "DRWRY" },
  { name: "BCBS Delaware", phone: "+1 800-633-2563 / +1 800-572-2872", stediId: "ILQUL" },
  { name: "BCBS DC", phone: "+1 800-842-5975", stediId: "OUVRH" },
  { name: "BCBS Florida", phone: "+1 800-727-2227", stediId: "ULXRI" },
  { name: "BCBS Georgia", phone: "+1 855-295-5840 (Professional) / +1 800-284-2609 (Facility)", stediId: "VDCLI" },
  { name: "BCBS Hawaii", phone: "+1 808-948-6330", stediId: "WKDLP" },
  { name: "BCBS Idaho", phone: "+1 866-482-2250 / +1 800-743-1871", stediId: "CJRPM" },
  { name: "BCBS Illinois", phone: "+1 800-972-8088", stediId: "DTQAZ" },
  { name: "BCBS Indiana", phone: "+1 800-676-2583", stediId: "AOQAR" },
  { name: "BCBS Iowa", phone: "+1 800-362-2218 / +1 800-552-3993", stediId: "PYJGI" },
  { name: "BCBS Kansas", phone: "+1 800-432-3990 / +1 800-432-0272 (Benefits/Eligibility)", stediId: "OKDNG" },
  { name: "BCBS Kentucky", phone: "+1 888-650-4133 / +1 800-282-1016", stediId: "DXTYZ" },
  { name: "BCBS Louisiana", phone: "+1 800-495-2583", stediId: "SXFQO" },
  { name: "BCBS Maine", phone: "+1 800-934-0331", stediId: "YJHSX" },
  { name: "BCBS Maryland", phone: "+1 800-842-5975", stediId: "GBPZQ" },
  { name: "BCBS Massachusetts", phone: "+1 800-882-2060", stediId: "EWDCI" },
  { name: "BCBS Michigan", phone: "+1 877-391-5921", stediId: "KRPCH" },
  { name: "BCBS Minnesota", phone: "+1 800-344-8525", stediId: "WLKCN" },
  { name: "BCBS Mississippi", phone: "+1 800-257-5825", stediId: "AGBDX" },
  { name: "BCBS Missouri", phone: "+1 888-571-9054 (Anthem Local) / +1 866-791-2292 (Blue Card)", stediId: "XUAZF" },
  { name: "BCBS Montana", phone: "+1 800-447-7828", stediId: "DBFST" },
  { name: "BCBS Nebraska", phone: "+1 800-635-0579 (IVR)", stediId: "OIAZC" },
  { name: "BCBS Nevada", phone: "+1 877-833-5742 / +1 855-854-1438", stediId: "ERSOT" },
  { name: "BCBS New Hampshire", phone: "+1 800-934-0331", stediId: "QCFIB" },
  { name: "BCBS New Jersey", phone: "+1 800-624-1110", stediId: "NRHDN" },
  { name: "BCBS New Mexico", phone: "+1 888-349-3706 (IVR)", stediId: "UDVWS" },
  { name: "BCBS New York", phone: "+1 800-552-6630", stediId: "OLQXL" },
  { name: "BCBS Western New York", phone: "+1 800-950-0051 (Traditional Plans) / +1 800-950-0052 (Managed Care Plans)", stediId: "HTLST" },
  { name: "BCBS Northeastern New York", phone: "+1 800-444-4552", stediId: "DUTFW" },
  { name: "BCBS North Carolina", phone: "+1 888-206-4697 / +1 800-672-7897 / +1 800-214-4844", stediId: "UPICO" },
  { name: "BCBS North Dakota", phone: "+1 800-368-2312", stediId: "AWBDZ" },
  { name: "BCBS Ohio", phone: "+1 888-290-9160 / +1 888-224-4902 / +1 855-854-1438", stediId: "BHNXS" },
  { name: "BCBS Oklahoma", phone: "+1 800-496-5774", stediId: "XOFIC" },
  { name: "BCBS Oregon", phone: "+1 866-227-0913 / +1 800-351-2370", stediId: "VSAZE" },
  { name: "BCBS Pennsylvania", phone: "+1 866-688-2242 (Professional Service) / +1 800-753-1276 (Facility Service) / +1 800-345-3806", stediId: "AJOXG" },
  { name: "BCBS Rhode Island", phone: "+1 800-230-9020", stediId: "ZINQS" },
  { name: "BCBS South Carolina", phone: "+1 800-868-2510", stediId: "NYNYD" },
  { name: "BCBS South Dakota", phone: "+1 800-774-3892 / +1 800-642-9273", stediId: "PYJGI" },
  { name: "BCBS Tennessee", phone: "+1 800-924-7141", stediId: "MPOJX" },
  { name: "BCBS Texas", phone: "+1 800-451-0287", stediId: "QDTRP" },
  { name: "BCBS Utah", phone: "+1 866-227-0913 / +1 800-351-2370", stediId: "HNRTE" },
  { name: "BCBS Vermont", phone: "+1 833-592-9956", stediId: "BIUKV" },
  { name: "BCBS Virginia", phone: "+1 800-842-5975", stediId: "DGOYK" },
  { name: "BCBS Washington", phone: "+1 866-227-0913 / +1 800-351-2370 / +1 800-722-4714", stediId: "BSNZF" },
  { name: "BCBS Wisconsin", phone: "+1 888-571-9055 / +1 833-578-4439", stediId: "VLFZU" },
  { name: "BCBS Wyoming", phone: "+1 800-442-2376", stediId: "XVMEF" },
  // Other Payers
  { name: "Aetna", phone: "+1 888-632-3862", hours: "8:00 AM - 6:00 PM (EST)", info: "Select option 1 for coverage and benefits OR option 3 for prior-auth requirements. Share the member's details and ask for the representative for assistance.", stediId: "HPQRS" },
  { name: "United Health Care", phone: "+1 877-842-3210", hours: "8:00 AM - 5:00 PM (EST)", info: "Ask for an agent through IVR to directly connect for assistance.", stediId: "KMQTZ" },
  { name: "United Health Care - UMR", phone: "+1 877-233-1800", hours: "8:00 AM - 8:00 PM (EST)", info: "Ask for an agent through IVR to directly connect for assistance.", stediId: "UWTOI" },
  { name: "United Health Care - Behavioral Health", phone: "+1 800-201-6991", hours: "8:00 AM - 8:00 PM (EST)", info: "Ask for an agent through IVR to directly connect for assistance.", stediId: "KMQTZ" },
  { name: "Cigna", phone: "+1 800-997-1654", hours: "9:00 AM - 8:00 PM (EST)", info: "Wait for the IVR recording to end, call automatically connects to the customer advocate.", stediId: "HGJLR" },
  { name: "Cigna - NALC Plan", phone: "+1 888-636-6252", hours: "9:00 AM - 8:00 PM (EST)", info: "Press 2 to get benefits and prior-auth details.", stediId: "HGJLR" },
  { name: "Healthnet", phone: "+1 800-641-7761", hours: "5:00 AM - 9:00 PM (PST)", info: "Fill out the provider's details, then ask for an agent through IVR to directly connect for assistance.", stediId: "PHRMB" },
  { name: "Palmetto GBA", phone: "+1 855-696-0705", hours: "8:00 AM - 5:00 PM (ET), Mon-Fri", info: "Press 1, then 3 to connect directly to an agent.", stediId: "KIZWB" },
  { name: "WellMed", phone: "+1 877-757-4440", hours: "9:00 AM - 9:00 PM, Mon-Fri", info: "For claims and authorization, press 1 to get prior-authorization information, following which you will directly connect to the agent.", stediId: "OVOSB" },
  { name: "MHS Ambetter", phone: "+1 877-687-1180", hours: "8:00 AM - 8:00 PM, Mon-Fri", info: "Go through the IVR to get benefits or prior-authorization details.", stediId: "IMJGY" },
  { name: "Humana", phone: "+1 800-457-4708", hours: "8:00 AM - 8:00 PM, Mon-Fri", info: "You will be directed to the customer service advocate after giving the complete provider's and member's details to the IVR.", stediId: "UYORK" },
  { name: "Quantum Health", phone: "+1 855-696-0705", hours: "8:30 AM - 5:30 PM (PST), Mon-Fri", info: "Press 1, then 3 to connect directly to an agent." },
  { name: "Care N Care Claims", phone: "+1 844-806-8216", info: "Press 1 for benefits and then press 2 to directly connect to the live customer representative.", stediId: "NOCQV" },
  { name: "Tricare East", phone: "+1 800-444-5445", hours: "8:00 AM - 7:00 PM (EST), Mon-Fri", info: "Press 1 and then press 2 for eligibility and benefits OR press 3 for prior authorization.", stediId: "IYHIG" },
  { name: "Tricare West", phone: "+1 844-866-9378", hours: "8:00 AM - 7:00 PM (EST), Mon-Fri", info: "Press 1 and then press 2 for eligibility and benefits OR press 3 for prior authorization.", stediId: "TJPRF" },
  { name: "Carelon (For Prior-Auth)", phone: "+1 800-252-2021", hours: "8:00 AM - 5:00 PM (CST), Mon-Fri", info: "Ask for an agent through IVR to directly connect for prior authorization information.", stediId: "SWTRT" },
  { name: "Imagine Health", phone: "+1 800-457-4708", hours: "8:30 AM - 4:30 PM, Mon-Fri" },
  { name: "CHAMPVA", phone: "+1 800-733-8387", hours: "8:05 AM - 7:30 PM (ET), Mon-Fri", stediId: "IJWQW" },
  { name: "Banker's Life Medicare Supplement", phone: "(800) 621-3724", hours: "8:00 AM - 8:00 PM (EST), Mon-Fri", stediId: "XYOKJ" },
  { name: "Cigna Healthgram", phone: "(800) 446-5439", hours: "9:00 AM - 8:00 PM, Mon-Fri", info: "Press 1, then 1 again, and then 4 to connect to a live representative.", stediId: "ELXGL" },
  { name: "Ambetter", phone: "(800) 442-1623", hours: "8:00 AM - 8:00 PM (CST), Mon-Fri", info: "Provide NPI to get connected to a representative.", stediId: "IMJGY" },
  { name: "Medicaid Superior Health Plan", phone: "+1 877-391-5921", hours: "8:00 AM - 8:00 PM, Mon-Fri", info: "Secondary insurance, provide details and you will get connected to the agent." },
  { name: "Silver Bach", phone: "+1 855-359-9999" },
  { name: "EviCore", phone: "+1 888-622-7329", hours: "7:00 AM - 7:00 PM (CST), Mon-Fri", stediId: "VKBON" },
  { name: "WebTPA", phone: "+1 800-477-8957", hours: "8:00 AM - 8:00 PM (EST), Mon-Fri", stediId: "WUOPN" },
  { name: "Anthem BC", phone: "855-641-4862 (4-digit prefixes with alphabet) / 800-444-2726 (3-digit prefixes)", hours: "8:00 AM - 8:00 PM (EST), Mon-Fri", info: "Provide NPI, Tax ID, Insurance ID and DOB to the IVR prompts. Then choose 1 for Eligibility and Benefits or 3 for Prior Auth info. After eligibility information is given, the IVR lets you choose 'Something Else' as an option which connects directly to a live representative.", stediId: "LAESW" },
  { name: "AARP", hours: "Weekdays 7:00 AM - 11:00 PM (ET), Saturday 9:00 AM - 5:00 PM (ET)", stediId: "DXOAW" },
  { name: "Tricare for Life SP", phone: "+1 866-773-0404", hours: "Mon 5:00 AM - Midnight, Tue-Thu 12:00 AM - Midnight, Fri 12:00 AM - 10:00 PM (CST)", stediId: "EPIVM" },
  { name: "Secure Horizon (UHC)", phone: "+1 800-718-7496", hours: "8:00 AM - 8:00 PM (CST), Mon-Sat", stediId: "LYJAI" },
  { name: "Aetna Senior Supplement", phone: "+1 800-264-4000", hours: "Mon-Fri 7:00 AM - 7:00 PM (CT)", stediId: "LUJFN" },
  { name: "Florida Blue", phone: "(855) 714-8894", hours: "8:00 AM - 8:00 PM (EST)", stediId: "ULXRI" },
  { name: "Clover Health", phone: "(800) 836-6890", hours: "8:00 AM - 8:00 PM", info: "Press 1, then 1 again, provide NPI to directly connect to a live Rep.", stediId: "VUHRT" },
  { name: "MedCost", phone: "+1 800-648-7563", hours: "8:00 AM - 5:30 PM (ET), Mon-Fri", stediId: "BHKCH" },
  { name: "Cigna Supplemental Benefits", phone: "+1 866-459-4272", hours: "8:00 AM - 8:30 PM (ET), Mon-Fri", stediId: "EFUFJ" },
  { name: "Wellcare", phone: "+1 866-822-1339", hours: "8:00 AM - 8:00 PM (ET), Mon-Fri", info: "Press 1 for English then you will get connected to the live representative automatically.", stediId: "EGASA" },
  { name: "Molina Healthcare CA", phone: "(888) 858-2150", hours: "8:00 AM - 6:00 PM (PT), Mon-Fri", stediId: "ZKMIW" },
];

async function fetchStediData(stediId: string): Promise<StediData> {
  const res = await fetch(
    `https://healthcare.us.stedi.com/2024-04-01/payer/${stediId}`,
    { headers: { Authorization: `Key ${STEDI_API_KEY}` } }
  );
  if (!res.ok) return {};
  const data = await res.json();
  const payer = data.payer;
  return {
    avatarUrl: payer?.avatarUrl,
    website: payer?.urls?.website,
  };
}

const PayerDirectory = () => {
  const [stediCache, setStediCache] = useState<Record<string, StediData>>({});

  useSEO({
    title: "Payer Directory | Talos",
    description:
      "Contact information for eligibility checks, benefit verification, and prior-authorization across major insurance payers.",
  });

  useEffect(() => {
    const uniqueIds = [...new Set(payers.map((p) => p.stediId).filter(Boolean))] as string[];

    // Fetch all unique stediIds in parallel
    Promise.all(
      uniqueIds.map(async (id) => {
        const data = await fetchStediData(id);
        return [id, data] as const;
      })
    ).then((results) => {
      const cache: Record<string, StediData> = {};
      for (const [id, data] of results) {
        cache[id] = data;
      }
      setStediCache(cache);
    });
  }, []);

  const getStedi = (stediId?: string): StediData => {
    if (!stediId) return {};
    return stediCache[stediId] || {};
  };

  const parsePhoneNumbers = (phone?: string) => {
    if (!phone) return [];
    // Split on " / " or ", " that separates distinct numbers/entries
    // But keep labels like "Phoenix:" attached to their numbers
    const parts = phone.split(/\s*[\/,]\s*(?=(?:[A-Z]|[+(]|\d))/);
    return parts.map((p) => p.trim()).filter(Boolean);
  };

  const formatWebsite = (url?: string) => {
    if (!url) return null;
    return url
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/$/, "");
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-2">
            Payer Directory
          </h1>
          <p className="text-sm text-zinc-500 mb-8 max-w-2xl">
            Contact information for eligibility checks, benefit verification,
            and prior-authorization across major insurance payers.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="overflow-x-auto rounded-xl border border-zinc-200 overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="px-4 py-3 font-semibold text-zinc-900 min-w-[220px]">
                    Company Name
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-900 min-w-[180px]">
                    <span className="inline-flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5" />
                      Toll Free Number
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-900 min-w-[180px]">
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      Operational Hours
                    </span>
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-900 min-w-[250px]">
                    <span className="inline-flex items-center gap-1.5">
                      <Info className="h-3.5 w-3.5" />
                      Additional Information
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {payers.map((payer, index) => {
                  const stedi = getStedi(payer.stediId);
                  const website = formatWebsite(stedi.website);

                  return (
                    <tr
                      key={index}
                      className="border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {stedi.avatarUrl ? (
                            <img
                              src={stedi.avatarUrl}
                              alt={payer.name}
                              className="h-6 w-6 rounded-md object-contain flex-shrink-0"
                            />
                          ) : (
                            <div className="h-6 w-6 rounded-md bg-zinc-100 flex-shrink-0" />
                          )}
                          {website ? (
                            <a
                              href={stedi.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-medium text-zinc-900 hover:text-blue-600 transition-colors"
                            >
                              {payer.name}
                              <ExternalLink className="h-3 w-3 text-zinc-400" />
                            </a>
                          ) : (
                            <span className="font-medium text-zinc-900">
                              {payer.name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {payer.phone ? (
                          <div className="flex flex-col gap-1">
                            {parsePhoneNumbers(payer.phone).map((num, i) => (
                              <span
                                key={i}
                                className="inline-block bg-zinc-100 text-zinc-700 font-mono text-xs px-2 py-1 rounded-md w-fit whitespace-nowrap"
                              >
                                {num}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-500 text-xs">
                        {payer.hours || "—"}
                      </td>
                      <td className="px-4 py-3 text-zinc-600 text-xs leading-relaxed">
                        {payer.info || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PayerDirectory;
